import { useState, useEffect, useCallback, useRef } from 'react'
import { isSoftDeleted } from 'app/api/client/softDelete'
import { Consult } from 'app/models/domain/consult'
import { ConsultDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import { consultQueryRepository } from 'app/api/consult/repository/ConsultQueryRepository'
import {
    consultSubscriptionManager,
    SubscriptionHandle,
} from 'app/api/consult/repository/ConsultSubscriptionManager'
import { useConsultEnrichment } from 'app/hooks/useConsultEnrichment'
import {
    sortByStartDateDesc,
    buildUnenrichedConsults,
} from 'app/hooks/consultListUtils'

export {
    sortByStartDateDesc,
    buildUnenrichedConsults,
} from 'app/hooks/consultListUtils'

const PAGE_SIZE = 30
const BATCH_SIZE = 4

export interface UseConsultListResult {
    consults: Consult[]
    isSynced: boolean
    isLoadingMore: boolean
    hasMore: boolean
    loadError: boolean
    fetchNextPage: () => void
}

/**
 * Replaces `subscribeToConsults` from ConsultListenerRepository.
 *
 * Lifecycle:
 * 1. Calls `consultQueryRepository.listByOwner()` for the initial page.
 * 2. Maps DTOs → Consult via `Consult.buildFromDto()` and emits Phase 1 (unenriched).
 * 3. Calls `enrichBatch()` for Phase 2 (patient + entities populated).
 * 4. Sets up create/update subscriptions that merge new DTOs and re-enrich.
 * 5. Exposes `fetchNextPage` for pagination.
 */
export function useConsultList(
    ownerSub: string | undefined
): UseConsultListResult {
    const [consults, setConsults] = useState<Consult[]>([])
    const [isSynced, setIsSynced] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [loadError, setLoadError] = useState(false)

    const { enrichBatch } = useConsultEnrichment()

    // Mutable refs to track state across async callbacks without re-renders
    const dtosRef = useRef<ConsultDto[]>([])
    const nextTokenRef = useRef<string | null>(null)
    const isFetchingRef = useRef(false)
    const isSyncedRef = useRef(false)
    const hasMoreRef = useRef(true)

    // ── Initial load ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!ownerSub) {
            return
        }

        let cancelled = false

        const loadInitialPage = async () => {
            try {
                // The byOwner GSI returns this owner's consults already sorted by
                // startDate (DESC via listByOwner), so the first page is the
                // genuinely-newest consults — no sparse-page scanning needed.
                const result = await consultQueryRepository.listByOwner(
                    ownerSub,
                    PAGE_SIZE,
                    null
                )
                if (cancelled) return

                nextTokenRef.current = result.nextToken
                hasMoreRef.current = result.nextToken !== null
                setHasMore(result.nextToken !== null)

                const sorted = sortByStartDateDesc(result.items)
                dtosRef.current = sorted

                // Phase 1: emit unenriched consults immediately
                const unenriched = buildUnenrichedConsults(sorted)
                setConsults(unenriched)
                setIsSynced(false)
                isSyncedRef.current = false

                // Phase 2: enrich in background
                await enrichBatch(
                    unenriched,
                    BATCH_SIZE,
                    (enriched, isComplete) => {
                        if (cancelled) return
                        setConsults([...enriched])
                        if (isComplete) {
                            setIsSynced(true)
                            isSyncedRef.current = true
                        }
                    }
                )
            } catch (error) {
                logger.error(
                    'Error loading initial consults',
                    error instanceof Error ? error : new Error(String(error)),
                    {
                        action: 'use_consult_list_initial_error',
                        metadata: { owner: ownerSub },
                    }
                )
                if (!cancelled) {
                    setLoadError(true)
                }
            }
        }

        loadInitialPage()

        return () => {
            cancelled = true
        }
    }, [ownerSub, enrichBatch])

    // ── Subscriptions ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!ownerSub) return

        const handles: SubscriptionHandle[] = []

        const handleDto = async (dto: ConsultDto, isDelete: boolean) => {
            console.error(
                '[DIAG] useConsultList.handleDto: id =',
                dto.id?.substring(0, 8),
                'isDelete =',
                isDelete,
                'current dtosRef count =',
                dtosRef.current.length
            )
            if (isDelete) {
                dtosRef.current = dtosRef.current.filter((d) => d.id !== dto.id)
                const unenriched = buildUnenrichedConsults(dtosRef.current)
                setConsults(unenriched)
                return
            }

            // Check if this is a meaningful update by comparing updatedAt.
            // AppSync sets updatedAt on every write (the model type has no
            // _version field, although the queries select it).
            const existingDto = dtosRef.current.find((d) => d.id === dto.id)
            const isNewConsult = !existingDto
            const versionChanged = existingDto?.updatedAt !== dto.updatedAt

            dtosRef.current = sortByStartDateDesc([
                ...dtosRef.current.filter((d) => d.id !== dto.id),
                dto,
            ])

            // Skip re-render if version hasn't changed (duplicate subscription event)
            if (!isNewConsult && !versionChanged) return

            // For new consults, emit unenriched immediately (Phase 1)
            // For updates, skip the unenriched render — the existing enriched
            // consult is already in the list, emitting unenriched would downgrade it
            // and trigger unnecessary re-enrichment in the detail hook.
            if (isNewConsult) {
                const unenriched = buildUnenrichedConsults(dtosRef.current)
                console.error(
                    '[DIAG] useConsultList.handleDto: unenriched count =',
                    unenriched.length,
                    'calling setConsults'
                )
                setConsults(unenriched)
            }

            // Phase 2: enrich the new/updated consult
            const currentConsults = isNewConsult
                ? buildUnenrichedConsults(dtosRef.current)
                : [
                      ...(consults.length > 0
                          ? consults
                          : buildUnenrichedConsults(dtosRef.current)),
                  ]
            const target = currentConsults.find((c) => c.id === dto.id)
            if (target) {
                await enrichBatch([target], 1, (enriched) => {
                    // Merge the enriched consult back into the full list
                    const enrichedTarget = enriched.find((c) => c.id === dto.id)
                    if (enrichedTarget) {
                        setConsults((prev) =>
                            prev.map((c) =>
                                c.id === dto.id ? enrichedTarget : c
                            )
                        )
                    }
                    setIsSynced(true)
                    isSyncedRef.current = true
                })
            }
        }

        // Refetch-on-poke (ACA-126): owner-auth subscriptions deliver a NULL
        // payload for updates written by the backend note pipeline (IAM lambdas),
        // so those onUpdate events carry no data and no id. Treat each as a
        // "something changed" poke and refetch this owner's recent consults via
        // the owner-authed query, feeding the fresh DTOs through handleDto (which
        // diffs updatedAt and only re-renders/re-enriches what actually changed).
        // Debounced to coalesce the burst of pokes from a single state transition.
        let pokeTimer: ReturnType<typeof setTimeout> | null = null
        const refetchRecent = async () => {
            try {
                const result = await consultQueryRepository.listByOwner(
                    ownerSub,
                    PAGE_SIZE,
                    null
                )
                for (const dto of sortByStartDateDesc(result.items)) {
                    await handleDto(dto, false)
                }
            } catch (error) {
                logger.error(
                    'Failed to refetch consults on subscription poke',
                    error instanceof Error ? error : new Error(String(error)),
                    { action: 'consult_list_poke_refetch_error' }
                )
            }
        }
        const schedulePokeRefetch = () => {
            if (pokeTimer) clearTimeout(pokeTimer)
            pokeTimer = setTimeout(() => {
                pokeTimer = null
                refetchRecent()
            }, 1500)
        }

        handles.push(
            consultSubscriptionManager.onConsultCreated(ownerSub, (dto) => {
                handleDto(dto, false).catch((error) => {
                    logger.error(
                        'Failed to handle created consult subscription',
                        error instanceof Error
                            ? error
                            : new Error(String(error)),
                        {
                            action: 'consult_list_create_sub_error',
                            metadata: { consultId: dto.id },
                        }
                    )
                })
            })
        )

        handles.push(
            consultSubscriptionManager.onConsultUpdated(
                ownerSub,
                (dto) => {
                    const isDelete = isSoftDeleted(dto)
                    handleDto(dto, isDelete).catch((error) => {
                        logger.error(
                            'Failed to handle updated consult subscription',
                            error instanceof Error
                                ? error
                                : new Error(String(error)),
                            {
                                action: 'consult_list_update_sub_error',
                                metadata: { consultId: dto.id },
                            }
                        )
                    })
                },
                schedulePokeRefetch
            )
        )

        return () => {
            if (pokeTimer) clearTimeout(pokeTimer)
            handles.forEach((h) => h.unsubscribe())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- consults is read for diagnostics; resubscribing on every list change would drop and recreate the AppSync subscriptions
    }, [ownerSub, enrichBatch])

    // ── Pagination ─────────────────────────────────────────────────────────
    const fetchNextPage = useCallback(() => {
        if (!ownerSub || !hasMoreRef.current || isFetchingRef.current) return

        isFetchingRef.current = true
        setIsLoadingMore(true)

        consultQueryRepository
            .listByOwner(ownerSub, PAGE_SIZE, nextTokenRef.current)
            .then(async (result) => {
                nextTokenRef.current = result.nextToken
                hasMoreRef.current = result.nextToken !== null
                setHasMore(result.nextToken !== null)

                dtosRef.current = sortByStartDateDesc([
                    ...dtosRef.current,
                    ...result.items,
                ])

                // Phase 1: emit all consults (existing enriched + new unenriched)
                const allUnenriched = buildUnenrichedConsults(dtosRef.current)
                isFetchingRef.current = false
                setIsLoadingMore(false)
                setConsults(allUnenriched)
                setIsSynced(false)
                isSyncedRef.current = false

                // Phase 2: enrich only the new page items
                const newConsults = buildUnenrichedConsults(result.items)
                await enrichBatch(newConsults, BATCH_SIZE, (_, isComplete) => {
                    const isFinalSync = isComplete && !hasMoreRef.current
                    setIsSynced(isFinalSync)
                    isSyncedRef.current = isFinalSync
                    setConsults([...allUnenriched])
                })
            })
            .catch((error) => {
                logger.error(
                    'Error fetching next page of consults',
                    error instanceof Error ? error : new Error(String(error)),
                    {
                        action: 'consult_list_next_page_error',
                        metadata: { owner: ownerSub },
                    }
                )
                isFetchingRef.current = false
                setIsLoadingMore(false)
                // nextToken preserved for retry
            })
    }, [ownerSub, enrichBatch])

    return {
        consults,
        isSynced,
        isLoadingMore,
        hasMore,
        loadError,
        fetchNextPage,
    }
}
